import React from 'react';
import {
  Grid,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Typography,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import { ExpandMore } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/_common/models/redux/store';
import HideInMobile from 'src/_common/quillElements/fragment/HideinMobile';
import ShowInMobile from 'src/_common/quillElements/fragment/ShowInMobile';
import SummaryTypes from './shared/SummaryTypes';
import SummaryRatio from './shared/SummaryRatio';
import { getOutputKey } from '../handlers/logic';
import { OutputExt } from '../models/store';
import {
  changeSummaryModeAction,
  userClickedModeExpansionPanelAction,
} from '../actions/controlActions';
import { ExtUnionGuard } from '../guards/union';
import { extSliderValues, absSliderValues } from '../mConstants';

const useStyles = makeStyles((theme) =>
  createStyles({
    expansionTitle: {
      fontSize: '15px',
      fontWeight: 600,
    },
    expansionTitleExt: {
      fontSize: '14px',
      fontWeight: 600,
      textAlign: 'center',
      display: 'block',
      width: '100%',
      margin: '0px 0px 10px',
    },
    disabled: {
      opacity: 0.4,
      cursor: 'none',
      pointerEvents: 'none',
    },
  })
);

const Accordion = withStyles({
  root: {
    border: 'none',
    background: 'rgba(227, 240, 231, 0.69)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: '0',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    backgroundColor: 'rgba(227, 240, 231, 0.69)',
    borderBottom: 'none',
    marginBottom: -1,
    padding: '0 12px',
    height: 40,
    '&$expanded': {
      maxHeight: 48,
      // height: 40,
      minHeight: 48,
    },
  },
  content: {
    '&$expanded': {
      margin: '0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    padding: '4px 12px',
  },
})(MuiAccordionDetails);

interface IProps {
  values?: Object;
  ratio?: number;
  summaryType?: string;
  isMobileControlsExpanded?: boolean;
  toggleMobileControls?: Function;
}

function MobileControls() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);
  const extLengthGauge = useSelector((state: RootState) => state.smzr.controls.extLengthGauge);
  const expanded = useSelector((state: RootState) => state.smzr.controls.isModePanelExpanded);

  const totalOutputSentences = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, absLengthGauge);
    const isExtType = ExtUnionGuard(state.smzr.output[outputKey]);
    return isExtType ? (state.smzr.output[outputKey] as OutputExt).sentenceSequence.length : 0;
  });
  const selectedKeywords = useSelector((state: RootState) => state.smzr.controls.selectedKeywords);

  const typeLabel = {
    ext: 'Key Sentences',
    abs: 'Paragraph',
  };

  let sliderValues;
  let sliderCurrentValue = absLengthGauge * 10;
  // let sliderLabel = absLengthGauge

  if (mode === 'ext') {
    // @TODO: sliderValue
    sliderValues = extSliderValues;
    sliderCurrentValue = extLengthGauge;
    // sliderLabel = extLengthGauge
    if (totalOutputSentences) sliderValues.max = totalOutputSentences;
    else extSliderValues.max = 20;
    if (sliderValues.max <= 2) sliderValues.min = 1;
  } else {
    sliderValues = absSliderValues;
  }

  return (
    <>
      <ShowInMobile>
        <Grid container>
          <Grid item xs={12} sm={12} md={12}>
            <Typography classes={{ root: classes.expansionTitleExt }}>Summary Modes</Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} style={{ textAlign: 'center' }}>
            <SummaryTypes
              onClick={() => dispatch(changeSummaryModeAction(mode === 'ext' ? 'abs' : 'ext'))}
              summaryType={mode}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            style={{ textAlign: 'center' }}
            className={mode === 'abs' && selectedKeywords.length > 0 ? classes.disabled : undefined}
          >
            <SummaryRatio
              currentValue={sliderCurrentValue}
              values={sliderValues}
              // labelValue={sliderLabel}
              sliderWidth="75%"
              step={
                totalOutputSentences && mode === 'ext'
                  ? Math.ceil(totalOutputSentences / extSliderValues.step)
                  : null
              }
              disabled={mode === 'abs' && selectedKeywords.length > 0}
            />
          </Grid>
        </Grid>
      </ShowInMobile>
      <HideInMobile>
        <Accordion
          square
          expanded={expanded}
          onChange={() => dispatch(userClickedModeExpansionPanelAction())}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography classes={{ root: classes.expansionTitle }}>
              Summary Modes ({typeLabel[mode]})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container alignItems="center" justifyContent="center" spacing={1}>
              <Grid item xs={12} sm className="qbp-textCenter">
                <SummaryTypes
                  onClick={() => dispatch(changeSummaryModeAction(mode === 'ext' ? 'abs' : 'ext'))}
                  summaryType={mode}
                />
              </Grid>
              <Grid item xs={12} sm>
                <SummaryRatio
                  currentValue={sliderCurrentValue}
                  values={sliderValues}
                  // labelValue={sliderLabel}
                  sliderWidth="75%"
                  step={
                    totalOutputSentences && mode === 'ext'
                      ? Math.ceil(totalOutputSentences / extSliderValues.step)
                      : null
                  }
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </HideInMobile>
    </>
  );
}

export default MobileControls;
